import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { PlaceOrderService } from '../../services/placeOrder/PlaceOrderService';
import { PlaceOrderDto } from '../../interfaces/PlaceOrderDto';

const SolicitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState<PlaceOrderDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentInvoiceUrl, setPaymentInvoiceUrl] = useState('');
  const [paymentDetail, setPaymentDetail] = useState('');

  const placeOrderCode = useMemo(() => {
    return (
      searchParams.get('placeOrderCode') ||
      searchParams.get('uuid') ||
      searchParams.get('codigo') ||
      searchParams.get('') ||
      ''
    );
  }, [searchParams]);

  useEffect(() => {
    if (!placeOrderCode) return;

    setLoading(true);
    setError('');
    setSuccess('');

    PlaceOrderService.getByCode(placeOrderCode)
      .then((response) => {
        setOrder(response.data || null);
      })
      .catch((err: any) => {
        setOrder(null);
        setError(err?.data?.detail || 'Não foi possível carregar a solicitação.');
      })
      .finally(() => setLoading(false));
  }, [placeOrderCode]);

  const handleApprove = async () => {
    if (!placeOrderCode) return;

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await PlaceOrderService.approve(placeOrderCode);
      setSuccess('Solicitação aprovada com sucesso.');
      const updated = await PlaceOrderService.getByCode(placeOrderCode);
      setOrder(updated.data || null);
    } catch (err: any) {
      const exception = err?.data?.details?.exception;
      const status = err?.data?.status;
      const title = err?.data?.title;
      const isPaymentInsufficient =
        exception === 'PaymentInsufficientException' ||
        status === 402 ||
        title === 'Payment Insufficient';

      if (isPaymentInsufficient) {
        const invoiceUrl =
          err?.data?.details?.payment?.invoiceUrl || err?.data?.details?.payment?.bankSlipUrl || '';
        setPaymentInvoiceUrl(invoiceUrl);
        setPaymentDetail(
          err?.data?.detail ||
            'Saldo insuficiente para aprovação. É necessário efetuar o pagamento.',
        );
        setPaymentModalOpen(true);
        setError('');
      } else {
        setError(err?.data?.detail || 'Não foi possível aprovar a solicitação.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!placeOrderCode) return;

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await PlaceOrderService.reject(placeOrderCode);
      setSuccess('Solicitação reprovada com sucesso.');
      const updated = await PlaceOrderService.getByCode(placeOrderCode);
      setOrder(updated.data || null);
    } catch (err: any) {
      setError(err?.data?.detail || 'Não foi possível reprovar a solicitação.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '';
    const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    const parsed = new Date(hasTimezone ? value : `${value}Z`);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  };

  const handleConfirm = async () => {
    if (confirmAction === 'approve') {
      await handleApprove();
    }

    if (confirmAction === 'reject') {
      await handleReject();
    }

    setConfirmAction(null);
  };

  const yesNo = (value?: boolean | null) => {
    if (value === null || value === undefined) return '';
    return value ? 'Sim' : 'Opcional';
  };

  const handleOpenInvoice = () => {
    if (!paymentInvoiceUrl) return;
    window.open(paymentInvoiceUrl, '_blank', 'noopener,noreferrer');
  };

  const statusLabel = order?.placeOrderApproveDate
    ? 'Aprovada'
    : order?.placeOrderReproveDate
    ? 'Reprovada'
    : 'Pendente';

  const dataRows = order
    ? [
        { label: 'Status', value: statusLabel },
        { label: 'Espaço', value: order.placeName || '' },
        { label: 'Tipo de evento', value: order.orderEventType || '' },
        { label: 'Data do evento', value: formatDate(order.eventDate) },
        { label: 'Convidados', value: order.invites ?? '' },
      ]
    : [];

  const decisionDateLabel = order?.placeOrderApproveDate
    ? 'Aprovada em'
    : order?.placeOrderReproveDate
    ? 'Reprovada em'
    : '';
  const decisionDateValue = order?.placeOrderApproveDate
    ? formatDateTime(order.placeOrderApproveDate)
    : order?.placeOrderReproveDate
    ? formatDateTime(order.placeOrderReproveDate)
    : '';

  const descriptionValue = order?.orderDescription || '';

  const resourceRows = order
    ? [
        { label: 'Possui ar condicionado ?', value: yesNo(order.hasAirConditioner) },
        { label: 'Possui buffet ?', value: yesNo(order.hasBuffet) },
        { label: 'Possui estacionamento ?', value: yesNo(order.hasParking) },
        { label: 'Possui ao ar livre ?', value: yesNo(order.hasOutdoor) },
        { label: 'Possui som ?', value: yesNo(order.hasSound) },
        { label: 'Possui múltiplas salas ?', value: yesNo(order.hasRoom) },
      ]
    : [];

  return (
    <Box
      sx={{
        maxWidth: 960,
        mx: 'auto',
        my: { xs: 8, md: 2 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3, border: '1px solid #eee' }}
      >
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 2,
              alignItems: { xs: 'flex-start', sm: 'center' },
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Solicitação de Interesse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Confira os dados e confirme a solicitação.
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                A aprovação confirma o interesse e libera o contato entre as partes.
              </Typography>
            </Box>
            {order && (
              <Box
                sx={{
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  px: 2,
                  py: 0.75,
                  borderRadius: 999,
                  bgcolor:
                    statusLabel === 'Aprovada'
                      ? '#E8F5E9'
                      : statusLabel === 'Reprovada'
                      ? '#FFEBEE'
                      : '#FFF8E1',
                  color:
                    statusLabel === 'Aprovada'
                      ? '#2E7D32'
                      : statusLabel === 'Reprovada'
                      ? '#C62828'
                      : '#F57C00',
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                }}
              >
                {statusLabel}
              </Box>
            )}
          </Box>

          {!placeOrderCode && (
            <Alert severity="warning">Nenhum código informado. Use a URL /solicitacao?=uuid.</Alert>
          )}

          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {!!error && <Alert severity="error">{error}</Alert>}
          {!!success && <Alert severity="success">{success}</Alert>}

          {!loading && order && (
            <Stack spacing={3}>
              <Alert severity="info">
                Revise atentamente os dados antes de aprovar ou reprovar.
              </Alert>
              <Divider />
              <Paper
                elevation={0}
                sx={{ bgcolor: '#f7f7f7', p: { xs: 2, md: 3 }, borderRadius: 2 }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  {dataRows.map((row) => (
                    <Box
                      key={row.label}
                      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {row.label}
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ lineBreak: 'anywhere' }}>
                        {row.value || '-'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Descrição
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ lineBreak: 'anywhere', fontSize: { xs: 14, sm: 16 } }}
                  >
                    {descriptionValue || '-'}
                  </Typography>
                </Box>
              </Paper>

              <Box>
                <Typography variant="h6" color="#1E88E5" fontWeight="bold" mb={2}>
                  Preferências do Cliente
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: 1.5,
                  }}
                >
                  {resourceRows.map((row, idx) => {
                    const label = row.label.replace('Possui ', '').replace(' ?', '');
                    const firstLetter = label.charAt(0).toUpperCase();
                    const rest = label.slice(1);
                    const isYes = row.value === 'Sim';
                    return (
                      <Box
                        key={`${row.label}-${idx}`}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: isYes ? '#C8E6C9' : '#E0E0E0',
                          bgcolor: isYes ? '#F1F8E9' : '#FAFAFA',
                          px: 1.5,
                          py: 1.25,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="#333"
                          fontWeight={600}
                          sx={{ fontSize: 12, mb: 0.5 }}
                        >
                          {firstLetter}
                          {rest}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={isYes ? '#2E7D32' : '#F57C00'}
                          sx={{ fontSize: 13, fontWeight: 700 }}
                        >
                          {row.value || '-'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Divider />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                  justifyContent: 'flex-end',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flex: 1, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                  {decisionDateLabel && decisionDateValue
                    ? `${decisionDateLabel} ${decisionDateValue}`
                    : 'Você pode alterar sua decisão enquanto a solicitação estiver pendente.'}
                </Typography>
                {!order.placeOrderApproveDate && !order.placeOrderReproveDate && (
                  <>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setConfirmAction('reject')}
                      disabled={actionLoading}
                      sx={{ minWidth: 200, width: { xs: '100%', sm: 'auto' } }}
                    >
                      Não Tenho Interesse
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setConfirmAction('approve')}
                      disabled={actionLoading}
                      sx={{ minWidth: 200, width: { xs: '100%', sm: 'auto' } }}
                    >
                      {actionLoading ? <CircularProgress size={18} /> : 'Tenho Interesse'}
                    </Button>
                  </>
                )}
              </Box>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Dialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmAction === 'approve' ? 'Aprovar solicitação' : 'Reprovar solicitação'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmAction === 'approve'
              ? 'Confirma a aprovação desta solicitação de interesse?'
              : 'Confirma a reprovação desta solicitação de interesse?'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmAction(null)}
            variant="outlined"
            disabled={actionLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={confirmAction === 'approve' ? 'primary' : 'error'}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={18} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Saldo Insuficiente</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {paymentDetail || 'Para aprovar esta solicitação é necessário efetuar o pagamento.'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Após a confirmação do pagamento, a solicitação será aprovada automaticamente.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPaymentModalOpen(false)} variant="outlined">
            Fechar
          </Button>
          <Button
            onClick={handleOpenInvoice}
            variant="contained"
            color="primary"
            disabled={!paymentInvoiceUrl}
          >
            Abrir fatura
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SolicitationPage;
